import { type Community, getInitials } from "@upsquad/shared";
import { Check, ChevronDown, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Avatar, Dropdown, Label, Tooltip } from "@/src/common/components/ui";
import { useCommunitySwitcher } from "@/src/community/contexts/community-switcher-context";
import { useCommunityNavigation } from "@/src/community/hooks/use-community-navigation";

interface CommunitySwitcherProps {
	communities: Community[];
	className?: string;
}

export function CommunitySwitcherTrigger({ communities }: CommunitySwitcherProps) {
	const { toggle, triggerRef } = useCommunitySwitcher();
	const currentSlug = window.location.hostname.split(".")[0];
	const currentCommunity = communities.find((c) => c.slug === currentSlug);

	return (
		<button
			ref={triggerRef}
			type="button"
			className="flex w-full items-center gap-2 rounded-lg p-1 transition-colors hover:bg-default-100"
			onClick={toggle}
		>
			<Avatar size="sm" className="shrink-0">
				<Avatar.Image src={currentCommunity?.image ?? undefined} alt={currentCommunity?.name ?? "Community"} />
				<Avatar.Fallback>{getInitials(currentCommunity?.name ?? "C")}</Avatar.Fallback>
			</Avatar>
			<span className="truncate font-semibold">{currentCommunity?.name}</span>
			<ChevronDown size={16} className="ml-auto shrink-0 text-foreground/50" />
		</button>
	);
}

export function CommunitySwitcherDropdown({ communities }: CommunitySwitcherProps) {
	const { t } = useTranslation("shell");
	const currentSlug = window.location.hostname.split(".")[0];
	const currentCommunity = communities.find((c) => c.slug === currentSlug);
	const { goToCreate, goToCommunity } = useCommunityNavigation();

	const handleSwitch = (slug: string) => {
		if (slug !== currentSlug) {
			goToCommunity(slug);
		}
	};

	return (
		<Dropdown>
			<Dropdown.Trigger>
				<button type="button" className="flex w-full items-center gap-2 rounded-lg p-2 transition-colors hover:bg-default-100">
					<Avatar size="sm" className="shrink-0">
						<Avatar.Image src={currentCommunity?.image ?? undefined} alt={currentCommunity?.name ?? "Community"} />
						<Avatar.Fallback>{getInitials(currentCommunity?.name ?? "C")}</Avatar.Fallback>
					</Avatar>
					<span className="truncate font-semibold">{currentCommunity?.name}</span>
					<ChevronDown size={16} className="ml-auto shrink-0 text-foreground/50" />
				</button>
			</Dropdown.Trigger>
			<Dropdown.Popover>
				<Dropdown.Menu
					aria-label={t("community.switch_community")}
					onAction={(key) => {
						if (key === "create") goToCreate();
						else handleSwitch(key.toString());
					}}
				>
					{communities.map((community) => (
						<Dropdown.Item key={community.slug} id={community.slug} textValue={community.name}>
							<div className="flex items-center gap-2">
								<Avatar size="sm" className="shrink-0">
									<Avatar.Image src={community.image ?? undefined} alt={community.name} />
									<Avatar.Fallback>{getInitials(community.name)}</Avatar.Fallback>
								</Avatar>
								<Label>{community.name}</Label>
								{community.slug === currentSlug && <Check size={14} className="ml-auto text-primary" />}
							</div>
						</Dropdown.Item>
					))}
					<Dropdown.Item id="create" textValue={t("community.create_community")}>
						<div className="flex items-center gap-2">
							<Plus size={16} />
							<Label>{t("community.create_community")}</Label>
						</div>
					</Dropdown.Item>
				</Dropdown.Menu>
			</Dropdown.Popover>
		</Dropdown>
	);
}

export function CommunitySwitcherPanel({ communities, className }: CommunitySwitcherProps) {
	const { t } = useTranslation("shell");
	const { isOpen, panelRef } = useCommunitySwitcher();
	const currentSlug = window.location.hostname.split(".")[0];
	const { goToCreate, goToCommunity } = useCommunityNavigation();

	const handleSwitch = (slug: string) => {
		if (slug !== currentSlug) {
			goToCommunity(slug);
		}
	};

	return (
		<aside
			ref={panelRef}
			className={`flex h-screen flex-col items-center border-r border-divider bg-white py-4 transition-all duration-300 dark:bg-neutral-900 ${isOpen ? "w-20" : "w-0 overflow-hidden"} ${className ?? ""}`}
		>
			<div className="flex flex-1 flex-col items-center gap-2 overflow-y-auto">
				{communities.map((community) => {
					const isActive = community.slug === currentSlug;
					return (
						<Tooltip key={community.slug}>
							<Tooltip.Trigger>
								<button type="button" className="relative shrink-0 rounded-full" onClick={() => handleSwitch(community.slug)}>
									<Avatar size="lg">
										<Avatar.Image src={community?.image ?? undefined} alt={community?.name ?? "Community"} />
										<Avatar.Fallback>{getInitials(community?.name ?? "C")}</Avatar.Fallback>
									</Avatar>
									{isActive && (
										<div className="absolute inset-0 z-10 flex items-center justify-center rounded-full bg-neutral-900/20">
											<Check size={16} color="white" />
										</div>
									)}
								</button>
							</Tooltip.Trigger>
							<Tooltip.Content>{community.name}</Tooltip.Content>
						</Tooltip>
					);
				})}
				<Tooltip>
					<Tooltip.Trigger>
						<button
							type="button"
							className="flex size-12 shrink-0 items-center justify-center rounded-full border-2 border-dashed border-foreground/30 text-foreground/50 transition-colors hover:border-primary hover:text-primary"
							onClick={goToCreate}
							aria-label={t("community.create_community")}
						>
							<Plus size={20} />
						</button>
					</Tooltip.Trigger>
					<Tooltip.Content>{t("community.create_community")}</Tooltip.Content>
				</Tooltip>
			</div>
		</aside>
	);
}
